if (!customElements.get("quick-add-modal")) {
  const ownedModals = new Map();
  let ownerObserver;
  let modalIdSuffix = 0;

  const registerOwner = (modal) => {
    if (!modal.owner || modal.owner === document.body) return;
    ownedModals.set(modal, modal.owner);
    if (!ownerObserver) {
      ownerObserver = new MutationObserver((records) => {
        if (!records.some((record) => record.removedNodes.length)) return;
        for (const [ownedModal, owner] of ownedModals) {
          if (!owner.isConnected) ownedModal.remove();
        }
      });
    }
    ownerObserver.observe(document.body, { childList: true, subtree: true });
  };

  const unregisterOwner = (modal) => {
    ownedModals.delete(modal);
    if (!ownedModals.size) ownerObserver?.disconnect();
  };

  customElements.define(
    "quick-add-modal",
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.onWindowKeyup = (evt) => {
          if (evt.code === "Escape" && this.hasAttribute("open")) {
            this.hide();
          }
        };
        this.onWindowResize = () => {
          if (!this.isConnected || !this.hasAttribute("open")) return;
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => {
            if (!this.isConnected || !this.hasAttribute("open")) return;
            this.destroySliders();
            this.initSliders();
          }, 200);
        };
      }

      connectedCallback() {
        if (!this.moved) {
          this.owner = this.closest(".card") || this.parentElement;
          this.ensureUniqueId();
        }
        super.connectedCallback();
        if (this.isConnected) registerOwner(this);
      }

      ensureUniqueId() {
        const originalId = this.id;
        const duplicates = [...document.querySelectorAll(`#${CSS.escape(originalId)}`)];
        if (!duplicates.some((element) => element !== this)) return;

        let uniqueId;
        do {
          uniqueId = `${originalId}-${++modalIdSuffix}`;
        } while (document.getElementById(uniqueId));
        this.id = uniqueId;
        this.modalContent.id = `QuickAddInfo-${uniqueId}`;
        this.querySelector('[id^="ModalClose-"]').id = `ModalClose-${uniqueId}`;
        if (this.owner === document.body) return;
        this.owner?.querySelectorAll("modal-opener").forEach((opener) => {
          if (opener.getAttribute("data-modal") === `#${originalId}`) {
            opener.setAttribute("data-modal", `#${uniqueId}`);
          }
        });
      }

      disconnectedCallback() {
        this.cancelRequest();
        this.cleanup();
        this.classList.remove("active");
        if (this.hasAttribute("open")) {
          this.openedBy = null;
          super.hide();
        }
        this.clearContent();
        this.clearError();
        unregisterOwner(this);
      }

      initSliders() {
        subSliderInit(this.modalContent, true);
        sliderInit(this.modalContent, true);
      }

      destroySliders() {
        this.modalContent
          .querySelectorAll(".js-media-list, .js-media-sublist")
          .forEach((element) => element.swiper?.destroy());
      }

      cleanup() {
        clearTimeout(this.resizeTimer);
        clearTimeout(this.activeTimer);
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        window.removeEventListener("keyup", this.onWindowKeyup);
        window.removeEventListener("resize", this.onWindowResize);
        this.removeEventListener("transitionend", this.onShowTransitionEnd);
        this.removeEventListener("transitionend", this.onHideTransitionEnd);
        this.destroySliders();
      }

      clearContent() {
        this.modalContent.replaceChildren();
        this.modalContent.classList.remove("has-scroll");
        this.modalContent.style.removeProperty("height");
        this.productElement = null;
      }

      setOpenerLoading(opener, loading) {
        opener.toggleAttribute("aria-disabled", loading);
        if (loading) opener.setAttribute("aria-disabled", "true");
        opener.classList.toggle("loading", loading);
        opener.querySelector(".loading-overlay__spinner")?.classList.toggle("hidden", !loading);
      }

      cancelRequest() {
        const request = this.request;
        if (!request) return;
        this.request = null;
        request.controller.abort();
        this.setOpenerLoading(request.opener, false);
      }

      isCurrentRequest(request) {
        return this.request === request && this.isConnected && request.opener.isConnected && (!this.owner || this.owner.isConnected);
      }

      clearError() {
        this.errorMessage?.remove();
        this.errorMessage = null;
      }

      showError(opener) {
        const message = window.themeStrings?.requestError || window.cartStrings?.error;
        if (!message) return;
        this.clearError();
        this.errorMessage = document.createElement("div");
        this.errorMessage.className = "product-form__error-message-wrapper";
        this.errorMessage.setAttribute("role", "alert");
        this.errorMessage.textContent = message;
        const container = opener.closest(".collection-product-card") || this.owner || opener.parentElement;
        container.appendChild(this.errorMessage);
      }

      hide(preventFocus = false) {
        this.cancelRequest();
        this.cleanup();
        const opener = this.openedBy;
        if (preventFocus) this.openedBy = null;

        this.classList.remove("active");
        super.hide();
        this.clearContent();

        if (!preventFocus && opener?.isConnected) {
          this.onHideTransitionEnd = (event) => {
            if (event.target !== this || this.hasAttribute("open")) return;
            (opener.closest(".card__links")?.nextElementSibling || opener).focus();
            this.removeEventListener("transitionend", this.onHideTransitionEnd);
          };
          this.addEventListener("transitionend", this.onHideTransitionEnd);
        }
      }

      show(opener) {
        if (this.request || this.hasAttribute("open")) return;
        this.cleanup();
        this.clearError();
        const request = { opener, controller: new AbortController() };
        this.request = request;
        this.setOpenerLoading(opener, true);

        return fetch(opener.getAttribute("data-product-url"), { signal: request.controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error("Quick-add request failed");
            return response.text();
          })
          .then(async (responseText) => {
            if (!this.isCurrentRequest(request)) return;
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              'section[id^="MainProduct-"]'
            );
            if (!this.productElement?.dataset.section || !this.productElement.querySelector(".product") || !this.productElement.querySelector(".product-form__buttons")) {
              throw new Error("Quick-add product markup is missing");
            }
            // 商品页按渲染树生成的组件样式和脚本不一定已在列表页加载。
            await Promise.all(
              [...responseHTML.querySelectorAll('link[rel="stylesheet"][href*="/compiled_assets/"], script[src*="/compiled_assets/snippet-scripts.js"]')].map(async (source) => {
                const isStylesheet = source.tagName === "LINK";
                // 编译脚本通过这个节点的标记选择组件；同一 URL 可能包含尚未启用的组件。
                while (!isStylesheet && document.getElementById("snippets-script")?.quickAddAssetLoad) {
                  await document.getElementById("snippets-script").quickAddAssetLoad;
                }
                const registry = isStylesheet ? null : document.getElementById("snippets-script");
                const previousSnippets = registry?.getAttribute("data-snippets") || "";
                const snippets = (source.getAttribute("data-snippets") || "").split(",").filter(Boolean);
                const newSnippets = snippets.filter((snippet) => !previousSnippets.split(",").includes(snippet));
                if (registry && !newSnippets.length) return;
                const attribute = isStylesheet ? "href" : "src";
                const url = source[attribute];
                const existing = isStylesheet && [...document.querySelectorAll('link[rel="stylesheet"]')]
                  .find((asset) => asset[attribute] === url);
                if (existing?.sheet) return;
                let snippetRegistry = registry;
                const loading = new Promise((resolve, reject) => {
                  const asset = existing || (isStylesheet ? source.cloneNode(true) : document.createElement("script"));
                  if (!isStylesheet) {
                    asset.src = url;
                    if (!snippetRegistry) {
                      asset.id = "snippets-script";
                      snippetRegistry = asset;
                    }
                    snippetRegistry.setAttribute("data-snippets", newSnippets.join(","));
                  }
                  asset.addEventListener("load", () => {
                    if (snippetRegistry) snippetRegistry.setAttribute("data-snippets", [...previousSnippets.split(",").filter(Boolean), ...newSnippets].join(","));
                    resolve();
                  }, { once: true });
                  asset.addEventListener("error", () => {
                    asset.remove();
                    if (registry) registry.setAttribute("data-snippets", previousSnippets);
                    reject(new Error("Quick-add component asset failed to load"));
                  }, { once: true });
                  if (!existing) document.head.appendChild(asset);
                });
                if (snippetRegistry) snippetRegistry.quickAddAssetLoad = loading;
                try {
                  await loading;
                } finally {
                  if (snippetRegistry) delete snippetRegistry.quickAddAssetLoad;
                }
              })
            );
            if (!this.isCurrentRequest(request)) return;
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(
              this.modalContent,
              this.productElement.innerHTML,
              opener
            );

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.updateImageSizes();
            this.preventVariantURLSwitching();
            this.initVariantPickers();
            super.show(opener);
            window.addEventListener("keyup", this.onWindowKeyup);
            window.addEventListener("resize", this.onWindowResize);
            this.initSliders();
            this.initDescriptionAccordion();
            this.initProductAccordion();
            this.setScrollClass(this.modalContent);
            this.activeTimer = setTimeout(() => {
              if (this.isConnected && this.hasAttribute("open")) this.classList.add("active");
            });

            this.onShowTransitionEnd = (event) => {
              if (event.target !== this || !this.hasAttribute("open")) return;
              trapFocus(this, this.querySelector(".quick-add-modal__content"));
              this.removeEventListener("transitionend", this.onShowTransitionEnd);
            };
            this.addEventListener("transitionend", this.onShowTransitionEnd);
          })
          .catch((error) => {
            if (!this.isCurrentRequest(request) || error.name === "AbortError") return;
            this.cleanup();
            this.classList.remove("active");
            if (this.hasAttribute("open")) super.hide();
            this.clearContent();
            this.showError(opener);
          })
          .finally(() => {
            if (this.request === request) {
              this.request = null;
              this.setOpenerLoading(opener, false);
            }
          });
      }

      setInnerHTML(element, html, opener) {
        element.innerHTML = html;
        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        // Read more button
        const moreBtn = document.createElement("a");
        moreBtn.innerHTML = `<span>${theme.quickviewMore}</span>`;
        moreBtn.setAttribute("href", opener.getAttribute("data-product-url"));
        moreBtn.setAttribute(
          "class",
          "product__full-details button button--underline"
        );
        if (
          element.querySelectorAll(".product__info-column") &&
          element.querySelectorAll(".product__info-column").length > 0
        ) {
          element.querySelector(".product-form__buttons").appendChild(moreBtn);
        } else {
          element.querySelector(".product-form__buttons").appendChild(moreBtn);
        }

        // Initialize buttons animations (global function initButtonsAnimation in global.js)
        initButtonsAnimation(element);
      }
      calcContainerHeight(element) {
        const topHeigh = this.querySelector(
          ".quick-add-modal__top"
        ).clientHeight;
        element.style.height = `calc(100% - ${
          topHeigh + 4
        }px)`;
      }

      toggleScrollClass(element) {
        const infoContainer = element?.querySelector(
          ".product__info-container"
        );

        const baseHeight = element.getBoundingClientRect().height;
        const productMedia = element.querySelector(".product__main");
        if (!productMedia || !infoContainer || !baseHeight) return;
        const productMediaType = productMedia.dataset.imageType;
        const heightRatio =
          productMediaType === "portrait"
            ? 1.266
            : productMediaType === "landscape"
            ? 0.75
            : 1;
        const imageHeight = productMedia.clientWidth * heightRatio;

        const toShowHeight = baseHeight - 16 - imageHeight;

        if (infoContainer.scrollHeight > toShowHeight) {
          element.classList.add("has-scroll");
        } else {
          element.classList.remove("has-scroll");
        }
      }

      setScrollClass(element) {
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver((entries) => {
          if (!this.isConnected || !this.hasAttribute("open")) return;
          const [entry] = entries;

          const dialog = entry.target.querySelector(
            ".quick-add-modal__content-info"
          );
          if (dialog) {
            this.calcContainerHeight(element);
            this.toggleScrollClass(dialog);
          }
        });

        if (element) {
          this.resizeObserver.observe(this);
        }
      }

      initDescriptionAccordion() {
        const description = this.modalContent.querySelector(".product__description .product-description")
          || this.modalContent.querySelector(".product__description");
        if (!description || description.querySelector("details, .station-tabs, .about__accordion-toggle")) return;
        const headingTag = window.Station?.Apps?.Tabs?.appSettings?.startTabElement || "h3";
        let content;
        [...description.children].forEach((element) => {
          if (element.tagName.toLowerCase() === headingTag.toLowerCase()) {
            const accordion = document.createElement("details");
            accordion.className = "quick-add-description";
            const summary = document.createElement("summary");
            summary.append(...element.childNodes);
            content = document.createElement("div");
            content.className = "quick-add-description__content";
            accordion.append(summary, content);
            element.replaceWith(accordion);
          } else if (content) {
            content.appendChild(element);
          }
        });
      }

      initProductAccordion() {
        const toggles = this.querySelectorAll(".about__accordion-toggle");

        toggles.forEach((toggle) => {
          toggle.addEventListener("click", (event) => {
            event.preventDefault();
            const description = toggle.nextElementSibling?.classList.contains(
              "about__accordion-description"
            )
              ? toggle.nextElementSibling
              : toggle.previousElementSibling?.classList.contains(
                  "about__accordion-description"
                )
              ? toggle.previousElementSibling
              : null;

            if (description) {
              if (!toggle.classList.contains("active")) {
                slideDown(toggle, description, 300); // func in global.js
              } else {
                slideUp(toggle, description, 300); // func in global.js
              }
            }
          });
        });
      }

      removeDOMElements() {
        const descriptionWrapper = this.productElement.querySelector(
          ".product__description-wrapper"
        );
        const popup = this.productElement.querySelectorAll(".product-popup");
        if (popup)
          popup.forEach((el) => {
            el.remove();
          });

        const shareButtons =
          this.productElement.querySelector(".share-buttons");
        if (shareButtons) shareButtons.remove();

        const sku = this.productElement.querySelector(".product__sku");
        if (sku) sku.remove();

        const breadcrumb = this.productElement.querySelector(".breadcrumb");

        if (breadcrumb) breadcrumb.remove();

        const tags = this.productElement.querySelector(".product-tags");
        if (tags) tags.remove();

        const countdown =
          this.productElement.querySelector(".product-countdown");
        if (countdown) countdown.remove();

        const bigProductModal =
          this.productElement.querySelector("product-modal");
        if (bigProductModal) bigProductModal.remove();

        const pickupAvailability = this.productElement.querySelector(
          ".pickup-availability"
        );
        if (pickupAvailability) pickupAvailability.remove();

        const iconWithTexts = this.productElement.querySelectorAll(
          ".product__text-icons"
        );
        if (iconWithTexts)
          iconWithTexts.forEach((el) => {
            el.remove();
          });

        const recommendations = this.productElement.querySelector(
          ".product-recommendations"
        );
        if (recommendations) recommendations.remove();

        const linkedProducts = this.productElement.querySelector(
          ".linked-products"
        );
        if (linkedProducts) linkedProducts.remove();
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickadd-${this.id}-${sectionId}`
          );
        this.productElement
          .querySelectorAll("variant-selects, variant-radios")
          .forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
          });
      }

      preventVariantURLSwitching() {
        if (this.modalContent.querySelector("variant-radios,variant-selects")) {
          this.modalContent
            .querySelector("variant-radios,variant-selects")
            .setAttribute("data-update-url", "false");
        }
      }

      initVariantPickers() {
        this.modalContent
          .querySelectorAll("variant-selects, variant-radios")
          .forEach((picker) => {
            if (typeof picker.updateOptions === "function") {
              picker.updateOptions();
            }
            if (typeof picker.updateMasterId === "function") {
              picker.updateMasterId();
            }
            if (typeof picker.reinitDropdownSelects === "function") {
              picker.reinitDropdownSelects();
            }
            if (typeof picker.syncAddToCartState === "function") {
              picker.syncAddToCartState();
            }
          });

        this.modalContent.querySelectorAll("product-form").forEach((form) => {
          if (typeof form.refreshSubmitElements === "function") {
            form.refreshSubmitElements();
          }
        });
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector(".product");
        const desktopColumns = product.classList.contains("product--columns");
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll(".product__media img");
        if (!mediaImages.length) return;

        let mediaImageSizes = "(min-width: 576px) 316px, calc(100vw - 4rem)";

        if (product.classList.contains("product--medium")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "605px");
        } else if (product.classList.contains("product--small")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "495px");
        }

        mediaImages.forEach((img) =>
          img.setAttribute("sizes", mediaImageSizes)
        );
      }
    }
  );
}
